/**
 * This file is meant to be a library for calling
 * feathers services to pick up data
 */

import moment from 'moment';
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import forOwn from 'lodash/forOwn';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import set from 'lodash/set';
import keyBy from 'lodash/keyBy';

import { Input } from '../components/common/form/FieldHelper';
import { BOOLEAN, RECORD } from '../constants/constant_custom_field_types';
import { STD_FIELDS_ENTITY } from '../constants/constant_standard_fields';
import { getDropdownList } from './apiCommon';
import {
  ITEM_LOCATION,
  SALE_TRANSACTION,
  COUNTRY,
  LANGUAGE,
  ENTITY,
} from '../constants/constant_doc_types';

export function newDate() {
  return moment.utc();
}

export function unshiftFields(fields, data, unshift) {
  unshift ? fields.unshift(data) : fields.push(data);
}

export function sortSublists(data, sublists) {
  if (data && sublists) {
    sublists.forEach((sublist) => {
      if (data[sublist.name]) {
        if (sublist.sort_by && sublist.sort_order) {
          data[sublist.name] = [
            ...orderBy(data[sublist.name], sublist.sort_by, sublist.sort_order),
          ];
        } else if (sublist.sort_by) {
          data[sublist.name] = [
            ...orderBy(data[sublist.name], sublist.sort_by, 'asc'),
          ];
        } else if (sublist.sort_order) {
          data[sublist.name] = [
            ...orderBy(
              data[sublist.name],
              'created_datetime',
              sublist.sort_order
            ),
          ];
        } else {
          data[sublist.name] = [...data[sublist.name].reverse()];
        }
      }
    });
  }

  return data;
}

export function newDateString() {
  return newDate().format();
}

export function toStringDate(object, startOf) {
  if (isEmpty(object)) return null;
  let date = moment(object).utc();
  if (startOf) date = date.startOf(startOf);
  return date.format();
}

export function toStringDateOnly(object) {
  if (isEmpty(object)) return null;
  return moment(object).startOf('day').format();
}

export function toCurrency(object, allowNegative, currencyCode) {
  if (isEmptyNumber(object)) {
    return null;
  }
  return Input.asCurrency(object, allowNegative, currencyCode);
}

export function toBoolean(object) {
  return object ? true : false;
}

export function toInteger(object) {
  if (isEmptyNumber(object)) {
    return null;
  }
  return Input.asInteger(object);
}

export function toPercent(object) {
  return toDecimal(object);
}

export function toDecimal(object) {
  if (isEmptyNumber(object)) {
    return null;
  }
  return Input.asDecimal(object);
}

export function toNumber(object) {
  let value = null;
  if (!isEmpty(String(object)) && !isNaN(object)) {
    value = Number(object);
  }
  return value;
}

export function toStringText(object) {
  if (object === '') return null;
  if (typeof object === 'string') return object.trim();
  if (typeof object === 'number') return String(object).trim();
  if (typeof object === 'boolean') return String(object).trim();
  return null;
}

export function toDataArray(object, dataFn) {
  let arr = null;
  if (Array.isArray(object) && !isEmpty(object)) {
    if (dataFn) {
      arr = object.map(dataFn);
    }
    arr = isEmpty(arr) ? null : arr;
  }
  return arr;
}

export function toObjectArray(object, filterFn) {
  let arr = null;
  if (Array.isArray(object) && !isEmpty(object)) {
    if (!filterFn) {
      filterFn = (obj) => {
        return !isEmpty(obj);
      };
    }
    arr = filter(object, filterFn);
    arr = isEmpty(arr) ? null : arr;
  }
  return arr;
}

export function toObjectIds(origArray=[], dataArray, field = '_id') {
  if (isEmpty(dataArray)) {
    return null;
  }

  const newArray = [];
  if (Array.isArray(dataArray)) {
    const origIdx = keyBy(origArray, field);
    dataArray.forEach((id) => {
      if (origIdx[id]) {
        newArray.push({
          ...origIdx[id],
        });
      } else {
        newArray.push({
          [field]: id,
        });
      }
    });
  }
  return newArray;
}

export function toObjectId(id) {
  if (!isEmpty(id)) {
    return { _id: id };
  }
  return null;
}

export function getMangoQueryDocs(res) {
  if (res && res.docs) {
    // console.log('util.getMangoQueryDocs:: ', res.docs);
    res.docs.map((doc) => {
      return convertDocIds(doc);
    });
    return res.docs;
  }
  return [];
}

export function convertDocIds(doc) {
  if (!doc) {
    return doc;
  }
  for (let p in doc) {
    if (p === 'custom_fields' || p === 'custom_tabs') {
      continue;
    }

    if (doc && doc.hasOwnProperty(p)) {
      let val = doc[p];
      if (val !== null && typeof val === 'object') {
        if (Array.isArray(val)) {
          let tempArr = [];
          val.forEach((valArr) => {
            if (valArr && typeof valArr === 'object') {
              if (valArr.hasOwnProperty('_id')) {
                if ((valArr._id || []).length > 0) {
                  tempArr.push(valArr._id || valArr.id);
                }
              }
            }
            if (valArr && typeof valArr === 'object') {
              convertDocIds(valArr);
            }
          });
          if (tempArr.length > 0) {
            doc[`${p}_ids`] = tempArr.slice();
          }
        } else {
          if (val._id || val.id) {
            doc[`${p}_id`] = val._id || val.id || '';
          } else {
            doc[`${p}`] = convertDocIds(val);
          }
        }
      }
    }
  }
  doc._id = doc.id || doc._id;
  return doc;
}

export function convertSingleDocId(doc) {
  if (typeof doc !== 'object' || !doc) return doc;

  if (Array.isArray(doc)) {
    doc = doc.map((value) => convertSingleDocId(value));
  } else {
    const keys = Object.keys(doc);

    for (let k = 0; keys.length > k; k++) {
      const key = keys[k];
      const value = doc[key];

      if ((value || {})._id || (value || {}).id) {
        doc[`${key}_id`] = value._id || value.id || null;
      }

      doc[key] = convertSingleDocId(value);
    }
  }

  return doc;
}

export function isActive(fromDate, toDate) {
  const day1 = new Date(fromDate);
  const day2 = new Date(toDate);
  const today = new Date();
  if (day2 instanceof Date && isFinite(day2)) {
    // check if today is between from and to dates
    if (today > day1 && today < day2) {
      return true;
    }
    return false;
  }
  // no toDate, then active is true
  return true;
}

export function isEmptyNumber(object) {
  if (object === null || object === undefined) {
    return true;
  }
  let o = object;
  if (typeof object === 'string') {
    o = object.trim();
  }
  return o === '';
}

export function concatRefId(alphaValue, numValue, maxLength) {
  const size = maxLength - alphaValue.length;
  const refId = alphaValue.toUpperCase() + padLeft(numValue, size);
  return refId;
}

export function incrementStr(str) {
  var arr = str.split('');
  var c;
  for (var i = arr.length - 1; i >= 0; i--) {
    c = (arr[i].charCodeAt(0) + 1) % 123;
    arr[i] = String.fromCharCode(c === 0 ? 97 : c);
    if (c !== 0) break;
  }
  return arr.join('');
}

export function toUtcDate(datetime) {
  return datetime ? moment(datetime).utc().format() : null;
}

function padLeft(text, n, str) {
  return Array(n - String(text).length + 1).join(str || '0') + text;
}

export function resolveRecordFilters(data_filters, record_filters) {
  if (isEmpty(data_filters)) return null;

  const filters = {};
  const regex = /_id|_ids/;

  forOwn(data_filters, (fValue, fKey) => {
    forEach(record_filters, (filter) => {
      const { type, name } = filter;

      if (fKey === name) {
        switch (type) {
          case 'dropdown':
            filters[`${name.replace(regex, '')}`] = toObjectId(fValue);
            break;

          case 'dropdown_multiple':
            filters[`${name.replace(regex, '')}`] = toObjectIds(fValue);
            break;

          default:
            filters[`${name}`] = fValue;
            break;
        }
      }
    });
  });

  return filters;
}

export function filterCustomFieldsByViewMode(doc, path, viewMode) {
  if (isEmpty(doc.custom_tabs)) {
    return doc;
  }

  let isViewMode;

  if (path) {
    const pathSplits = path.split('/');
    isViewMode =
      pathSplits[pathSplits.length - 2] === 'view' || pathSplits.length === 3;
  } else {
    isViewMode = viewMode;
  }

  if (isViewMode) {
    doc.custom_tabs = doc.custom_tabs
      .map((customTab) => ({
        ...customTab,
        custom_fields: customTab.custom_fields.filter(
          ({ field_name, field_type, reference_id }) => {
            const index = `cust_${field_name}_${reference_id}`;
            const value = (doc.custom_fields || {})[index];

            return customTab.custom_form || field_type === BOOLEAN || value;
          }
        ),
      }))
      .filter(({ custom_fields }) => custom_fields.length);
  }

  return doc;
}

export async function attachRecordListsToCustomForms(app, customTabs) {
  if (isEmpty(customTabs)) {
    return;
  }

  const stdRecordTypes = [COUNTRY, LANGUAGE];
  const recordLists = {};

  for (const tab of customTabs) {
    let { custom_form } = tab;

    if (isEmpty(custom_form)) {
      continue;
    }

    const { custom_fields } = tab;
    const recordFields = (custom_fields || []).filter(
      ({ field_type }) => field_type === RECORD
    );

    const recordTypes = map(recordFields, 'record_type');

    for (const recordType of [...stdRecordTypes, ...recordTypes]) {
      let recordList = recordLists[recordType];

      if (isEmpty(recordList)) {
        const dropdownType = [
          'contact',
          'customer',
          'employee',
          'supplier',
        ].includes(recordType)
          ? `entity/${recordType}/dropdown`
          : `${recordType}/dropdown`;

        let fields = ['id', 'name'];

        if (recordType === ITEM_LOCATION) {
          fields = ['id'];
        } else if (recordType === SALE_TRANSACTION) {
          fields = ['id', 'reference_id'];
        }

        const query = {
          dropdown_type: dropdownType,
          searchParams: {
            fields,
            no_limit: true,
          },
        };

        recordList = await getDropdownList(app, { query });
        recordList = isEmpty(recordList)
          ? []
          : map(recordList, (record) => {
              let name = record['name'];

              if (recordType === ITEM_LOCATION) {
                name = `${record.item.name} : ${record.location.name}`;
              } else if (recordType === SALE_TRANSACTION) {
                name = record['reference_id'];
              }

              return {
                name,
                id: record.id,
              };
            });

        recordLists[recordType] = recordList;
      }

      custom_form = set(custom_form, `record_lists.${recordType}`, recordList);
    }

    tab.custom_form = custom_form;
  }

  return customTabs;
}

export const getStdFieldKey = (fieldName) => `std_${fieldName}`;
const getAddressDetail = (doc) => {
  const addressDetails = !isEmpty(doc.address_details) ? doc.address_details : [{}];
  const addressDetailIndex = addressDetails.length - 1;
  const addressDetail = addressDetails[addressDetailIndex];
  return { addressDetail, addressDetailIndex };
};
const getContactNumber = (doc) => {
  const contactNumbers = !isEmpty(doc.contact_numbers) ? doc.contact_numbers : [{}];
  const contactNumberIndex = contactNumbers.length - 1;
  const contactNumber = contactNumbers[contactNumberIndex];
  return { contactNumber, contactNumberIndex };
};

export function attachStandardFieldValuesToCustomForm(doc, docType) {
  const customFields = doc.custom_fields || {};

  if (docType === ENTITY) {
    for (const fieldName of STD_FIELDS_ENTITY) {
      const fieldKey = getStdFieldKey(fieldName);

      switch (fieldName) {
        case 'first_name':
        case 'last_name':
        case 'email':
        case 'gender':
          customFields[fieldKey] = doc[fieldName];
          break;
        case 'address1':
        case 'address2':
        case 'city':
        case 'country':
        case 'zipcode': {
          const { addressDetail } = getAddressDetail(doc);

          let value = addressDetail[fieldName];

          if (fieldName === 'country') {
            value = addressDetail.country_id;
          }

          customFields[fieldKey] = value || null;

          break;
        }
        case 'contact_number': {
          const { contactNumber } = getContactNumber(doc);

          customFields[fieldKey] = contactNumber.value || null;

          break;
        }
        case 'date_of_birth': {
          const dateOfBirth = doc[fieldName];

          customFields[fieldKey] = dateOfBirth
            ? moment(dateOfBirth).format('YYYY-MM-DD')
            : undefined;

          break;
        }
        case 'preferred_language':
          customFields[fieldKey] = doc.preferred_language_id;
          break;
        default:
          break;
      }
    }
  }

  return customFields;
}

/**
 * compact a given path / paths
 *
 * @param {array || (object && !null)} compactee
 * @param {string} path
 *
 */
export function compactByPath(compactee, path) {
  const compacteeCopy = { ...compactee };
  if (typeof path === 'string') {
    set(compacteeCopy, path, compact(get(compacteeCopy, path)));
    return compacteeCopy;
  } else if (Array.isArray(path)) {
    path.forEach((p) => {
      set(compactee, p, compact(get(compacteeCopy, p)));
    });
    return compacteeCopy;
  } else {
    return compacteeCopy;
  }
}
